import type { Reference } from '@apollo/client';
import { useApolloClient, useMutation } from '@apollo/client';
import moment from 'moment';
import { useCallback, useEffect } from 'react';
import type { VariablesOf } from '@graphql-typed-document-node/core';
import { ACTION_POST_READ, CATEGORY_PRODUCT } from '../constants/analytics';
import { gql } from '../graphql/generated/gql';
import { track } from '../utils/analytics';
import useAppSelector from '../app/redux/useAppSelector';

const INACTIVITY_TIMEOUT = 30000;
const MIN_ACTIVITY_READ = 10000;
const MIN_ANALYTICS_READ = 30000;

const READ_POST_MUTATION = gql(`
  mutation readPost(
    $postId: ID!
    $time: Int!
    $queryId: String
    $resultId: String
  ) {
    readPost(id: $postId, time: $time, queryId: $queryId, resultId: $resultId) {
      id
    }
  }
`);

const postReadUpdateFragment = gql(`
  fragment PostReadUpdateFragment on Post {
    # eslint-disable-next-line slab-graphql/no-unused-fields
    userViewedAt
  }
`);

export const useReadPost = () => {
  const [readPost] = useMutation(READ_POST_MUTATION);
  const currentUser = useAppSelector((state) => state.session.user);

  return useCallback(
    (variables: VariablesOf<typeof READ_POST_MUTATION>) => {
      const { postId } = variables;
      return readPost({
        variables,
        optimisticResponse: { readPost: { id: postId } },
        update: (cache) => {
          const postRef = cache.identify({
            id: postId,
            __typename: 'Post',
          });

          cache.updateFragment(
            { id: postRef, fragment: postReadUpdateFragment },
            (existing) =>
              existing && { ...existing, userViewedAt: moment().toISOString() },
          );

          cache.modify({
            id: cache.identify(currentUser),
            fields: {
              viewedPosts(postConnection: { edges: { node: Reference }[] }) {
                const edges = postConnection.edges.toSorted(
                  ({ node: { __ref: aRef } }, { node: { __ref: bRef } }) => {
                    // move the post to the top
                    if (aRef === postRef) return -1;
                    if (bRef === postRef) return 1;
                    return 0;
                  },
                );

                return {
                  ...postConnection,
                  edges,
                };
              },
            },
          });
        },
      }).catch(() => {});
    },
    [readPost, currentUser],
  );
};

const usePostActivityTracker = (
  postId: string | null,
  queryId?: string,
  resultId?: string,
) => {
  const currentUser = useAppSelector((state) => state.session.user);
  const client = useApolloClient();
  const readPost = useReadPost();

  useEffect(() => {
    if (!postId) {
      return undefined;
    }
    let readStart: number | null = null;
    let lastActive: number | null = null;
    let inactiveInterval: NodeJS.Timeout | null = null;

    const sendRead = (endTime: number) => {
      if (readStart === null) return;
      const time = endTime - readStart;
      if (time > MIN_ACTIVITY_READ) {
        readPost({ postId, time, queryId, resultId });
      }
      if (time > MIN_ANALYTICS_READ) {
        track(ACTION_POST_READ, {
          category: CATEGORY_PRODUCT,
          label: postId,
          value: time,
        });
      }
    };

    const onInactive = () => {
      const now = moment().valueOf();
      if (lastActive !== null && now - lastActive > INACTIVITY_TIMEOUT * 1.2) {
        // Wake detected
        sendRead(lastActive);
      } else {
        sendRead(now);
      }
      if (inactiveInterval) {
        clearInterval(inactiveInterval);
      }
      inactiveInterval = null;
      readStart = null;
    };

    const onActive = () => {
      readStart = moment().valueOf();
      lastActive = moment().valueOf();
      inactiveInterval = setInterval(() => {
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        if (moment().valueOf() - lastActive! > INACTIVITY_TIMEOUT) {
          onInactive();
        }
      }, INACTIVITY_TIMEOUT);
    };

    const onActivity = () => {
      if (readStart === null) {
        onActive();
      }
      const now = moment().valueOf();
      if (lastActive !== null && now - lastActive > INACTIVITY_TIMEOUT * 1.2) {
        // Wake detected
        sendRead(lastActive);
        readStart = now;
      }
      lastActive = now;
    };

    onActive();

    document.addEventListener('mousemove', onActivity);
    document.addEventListener('touchstart', onActivity);

    return () => {
      onInactive();
      if (inactiveInterval) {
        clearInterval(inactiveInterval);
      }
      document.removeEventListener('mousemove', onActivity);
      document.removeEventListener('touchstart', onActivity);
    };
  }, [postId, queryId, resultId, readPost, client, currentUser]);
};

interface PostActivityTrackerProps {
  postId: string | null;
  queryId?: string;
  resultId?: string;
}

const PostActivityTracker = ({
  postId,
  queryId,
  resultId,
}: PostActivityTrackerProps) => {
  usePostActivityTracker(postId, queryId, resultId);

  return null;
};

export default PostActivityTracker;
